import { memo, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import AnimationContainer from '@/components/animation-container';
import CoverImage from '@/components/cover-image';
import { QSvg, JumpLinkOfQuestionSourceSvg } from '@/components/svg-icons/qa-records';
import { KnowledgeSliceVideoIconSvg, KnowledgeSliceDocumentIconSvg } from '@/components/svg-icons/dialogue-area';
import { SwiperSwitchRightSvg, SwiperSwitchLeftSvg } from '@/components/svg-icons/home';
import { IKnowledgeSectionItem, ISubjectSearch } from '@/types/models/qa';
import { getGlobalStore } from '@/store/global';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import { useThrottleFn } from 'ahooks';
import { check_care_auth } from '@/apis/qa';
import { getQaStore } from '@/store/qa';
import { clickSubjectSearchDataSensors } from '@/utils/sensors';
import classNames from 'classnames';
import 'swiper/css/pagination';
import 'swiper/css';
import './index.scss';

const SubjectSearch = ({ recordId, qaList, knowledgeSectionList }: Required<ISubjectSearch>) => {
    const scrollContainer = useRef<HTMLDivElement | null>(null);
    const [leftBtnVisible, setLeftBtnVisible] = useState(false);
    const [rightBtnVisible, setRightBtnVisible] = useState(false);
    const [activeIndex, setActiveIndex] = useState<number>();

    const newQaList = useMemo(() => {
        if (!qaList.length) return [];
        return new Array(Math.ceil(qaList.length / 3)).fill(null).map((_, index) => {
            return qaList.slice(index * 3, (index + 1) * 3);
        });
    }, [qaList]);

    const handleScroll = (direction: 'left' | 'right') => {
        let scrollDistance = scrollContainer.current!.scrollLeft;
        if (direction === 'left') {
            scrollDistance -= 140;
            scrollDistance = scrollDistance < 0 ? 0 : scrollDistance;
        } else {
            scrollDistance += 140;
            scrollDistance = scrollDistance > scrollContainer.current!.scrollWidth ? scrollContainer.current!.scrollWidth : scrollDistance;
        }
        scrollContainer.current?.scrollTo({ left: scrollDistance, behavior: 'smooth' });
    };

    const handleOpenDetailDrawer = (qaId: string) => {
        clickSubjectSearchDataSensors({ dataType: 'QA', havePermission: true, recordId });
        getGlobalStore()?.publish('QuestionDetailDrawer', { type: 'show', qaId });
    };

    const handleViewKnowledgeSliceDetail = async (item: IKnowledgeSectionItem, index: number) => {
        setActiveIndex(index);
        const { projectId, subjectId, courseId } = getQaStore()!.qaConfig;
        const { result } = await check_care_auth({ projectId: projectId!, subjectId: subjectId!, courseId });
        clickSubjectSearchDataSensors({ dataType: item.resourceType === 0 ? '视频' : '知识点', havePermission: result, recordId });
        if (!result) {
            getGlobalStore()?.publish('WarningModal', { type: 'no_view_permission' });
            return;
        }
        if (item.resourceType === 0) {
            // console.log(item);
            getGlobalStore()?.publish('VideoPlayerModal', {
                type: 'open',
                title: item.title,
                params: { videoId: item.resource, autoplay: true },
                recordId: item.recordId,
            });
        } else {
            getGlobalStore()?.publish('KnowledgePointsDrawer', {
                type: 'show',
                title: item.title,
                content: item.resource,
                recordId: item.recordId,
            });
        }
    };

    const { run: handleScrollX } = useThrottleFn(
        () => {
            const { scrollLeft, clientWidth, scrollWidth } = scrollContainer.current!;
            // console.log('handleScrollX', scrollLeft + clientWidth, scrollWidth);
            setLeftBtnVisible(scrollLeft > 0);
            // 这里减1是为了解决在windows电脑上，左侧结果存在浮点数的问题
            setRightBtnVisible(scrollLeft + clientWidth < scrollWidth - 1);
        },
        { wait: 100 }
    );

    const { run: handleResizeChange } = useThrottleFn(
        () => {
            const { clientWidth, scrollWidth } = scrollContainer.current!;
            // console.log('handleResizeChange', scrollLeft, clientWidth, scrollWidth);
            if (clientWidth === scrollWidth) {
                setLeftBtnVisible(false);
                setRightBtnVisible(false);
            } else {
                handleScrollX();
            }
        },
        { wait: 100 }
    );

    const resizeObserverRef = useRef<ResizeObserver>();

    useLayoutEffect(() => {
        return () => {
            if (scrollContainer.current) {
                resizeObserverRef.current?.unobserve(scrollContainer.current);
                scrollContainer.current.removeEventListener('scroll', handleScrollX);
            }
        };
    }, []);

    useEffect(() => {
        setTimeout(() => {
            if (scrollContainer.current) {
                handleResizeChange();
                resizeObserverRef.current = new ResizeObserver(handleResizeChange);
                resizeObserverRef.current.observe(scrollContainer.current);
                scrollContainer.current.addEventListener('scroll', handleScrollX);
            }
        }, 500);
    }, []);

    return (
        <div className="respondent-container">
            <AnimationContainer className="subject-search" type="enter_from_the_left">
                <div className="subject-search__title">学科搜索</div>
                {newQaList.length > 0 && (
                    <Swiper
                        spaceBetween={30}
                        pagination={{
                            clickable: true,
                        }}
                        modules={[Pagination]}
                        className="subject-search__swiper"
                    >
                        {newQaList.map((childList, index) => {
                            const key = index.toString();
                            return (
                                <SwiperSlide key={key}>
                                    {childList.map((childItem, childIndex) => (
                                        <div
                                            key={childItem.qaId}
                                            className="subject-search__item"
                                            onClick={() => handleOpenDetailDrawer(childItem.qaId)}
                                        >
                                            <QSvg className="subject-search__item-icon1" />
                                            <div
                                                className={classNames(
                                                    'subject-search__content',
                                                    childIndex < childList.length - 1 && 'subject-search__content--border-bottom'
                                                )}
                                            >
                                                <span>{childItem.qaTitle}</span>
                                            </div>
                                            <JumpLinkOfQuestionSourceSvg className="subject-search__item-icon2" />
                                        </div>
                                    ))}
                                </SwiperSlide>
                            );
                        })}
                    </Swiper>
                )}
                {knowledgeSectionList.length > 0 && (
                    <>
                        <div className="subject-search__knowledge-slice-title" />
                        <div className="subject-search__knowledge-slice-container">
                            {leftBtnVisible && (
                                <>
                                    <div className="subject-search__knowledge-slice-left-overlay" />
                                    <div className="subject-search__knowledge-slice-left" onClick={() => handleScroll('left')}>
                                        <SwiperSwitchLeftSvg />
                                    </div>
                                </>
                            )}
                            {rightBtnVisible && (
                                <>
                                    <div className="subject-search__knowledge-slice-right-overlay" />
                                    <div className="subject-search__knowledge-slice-right" onClick={() => handleScroll('right')}>
                                        <SwiperSwitchRightSvg />
                                    </div>
                                </>
                            )}
                            <div className="subject-search__knowledge-slice-scroll-container" ref={scrollContainer}>
                                <div className="subject-search__knowledge-slice">
                                    {knowledgeSectionList.map((item, index) => {
                                        return (
                                            <div
                                                key={item.recordId}
                                                className={classNames(
                                                    'subject-search__knowledge-slice-item',
                                                    index === activeIndex && 'subject-search__knowledge-slice-item--actived'
                                                )}
                                                onClick={() => handleViewKnowledgeSliceDetail(item, index)}
                                            >
                                                <CoverImage
                                                    url={item.coverImageUrl}
                                                    className={classNames(
                                                        'subject-search__knowledge-slice-cover',
                                                        item.resourceType === 0 && 'subject-search__knowledge-slice-cover--video'
                                                    )}
                                                ></CoverImage>
                                                <div className="subject-search__knowledge-slice-describe">
                                                    <div className="subject-search__knowledge-slice-describe-icon-container">
                                                        {item.resourceType === 0 && (
                                                            <KnowledgeSliceVideoIconSvg className="subject-search__knowledge-slice-describe-icon" />
                                                        )}
                                                        {item.resourceType === 1 && (
                                                            <KnowledgeSliceDocumentIconSvg className="subject-search__knowledge-slice-describe-icon" />
                                                        )}
                                                    </div>
                                                    {item.title}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </AnimationContainer>
        </div>
    );
};

export default memo(SubjectSearch);
